type Entry = {
  sector: string
  outcome: string
  bullets: string[]
  scope: string[]
  savings: {
    time: string
    cost: string
    basis: string
  }
}

const entries: Entry[] = [
  {
    sector: 'Legacy platform modernization',
    outcome:
      'A multi-module product marked for full rewrite. Conventional plan: three to four months with two backend engineers, two frontend engineers, one QA, plus product and delivery management.',
    bullets: [
      'With Compound the same rewrite completed in one week — one senior engineer and one product lead',
      'Project memory, method packs, and orchestrated delivery collapsed discovery, implementation, and verification into a single measured traverse',
      'Senior review remained on every merge; speed came from compounded context, not unchecked generation',
    ],
    scope: ['Rewrite plan', 'Domain adoption', 'Senior-led merges', 'Handover notes'],
    savings: {
      time: '~11–15 weeks calendar · ~20 person-months of squad effort',
      cost: '~USD 180–250k avoided',
      basis:
        'Versus a 3–4 month plan at six contributors; Compound run: one week, two people. Blended fully-loaded cost assumed for mixed seniority.',
    },
  },
  {
    sector: 'B2B SaaS security pass',
    outcome:
      'Pre-release hardening across API surface, auth edges, and regression harness — findings as an actionable trail with evidence, not a slide deck.',
    bullets: [
      'Method packs for review and pentest-style checks ran as an orchestrated wave',
      'Fixes landed against the same project memory that discovered them',
      'Retest notes closed the loop before release',
    ],
    scope: ['Threat-oriented review', 'Fix trail', 'Retest notes'],
    savings: {
      time: '~3–5 weeks calendar',
      cost: '~USD 45–80k avoided',
      basis:
        'Versus a typical external review cycle plus a multi-week internal fix/retest loop before release.',
    },
  },
  {
    sector: 'Developer platform enablement',
    outcome:
      'Launch blocked on guides, API manuals, and internal runbooks that had drifted from the shipped system.',
    bullets: [
      'Content and engineering skills shared one project memory',
      'Docs matched what actually shipped instead of an aspirational outline',
      'Institutional decisions from the build survived into the manuals',
    ],
    scope: ['User guides', 'API manuals', 'Ops runbooks'],
    savings: {
      time: '~4–6 weeks calendar',
      cost: '~USD 35–60k avoided',
      basis:
        'Versus a dedicated docs surge (writers + engineering review) that usually trails the build by a full cycle.',
    },
  },
  {
    sector: 'Internal tools under constraint',
    outcome:
      'Small senior team, fixed calendar, no room for agency theatre — greenfield MVP with analysis, build, and e2e in one continuous field.',
    bullets: [
      'Adaptive skills covered the spine from spec to verified slices',
      'Working memory kept the traverse intact across sessions',
      'Institutional memory stopped decisions from resetting every chat',
    ],
    scope: ['Spec spine', 'Vertical slices', 'E2E suite', 'Release notes'],
    savings: {
      time: '~6–8 weeks calendar',
      cost: '~USD 60–95k avoided',
      basis:
        'Versus an 8–12 week small-squad MVP plan compressed into roughly three weeks of continuous Compound traverse.',
    },
  },
]

export function CompoundRegister() {
  return (
    <section id="register" className="bg-sheet">
      <div className="mx-auto max-w-[100rem] px-6 pt-6 pb-12 lg:px-8 lg:pt-7 lg:pb-16">
        <div className="cell overflow-hidden">
          <div className="flex flex-col gap-2 border-b-2 border-ink px-5 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-7">
            <h2 className="section-title text-ink">Selected cases</h2>
            <p className="text-sm leading-relaxed text-ink-muted sm:max-w-xs sm:text-right">
              Representative work, anonymized. Savings are estimates against the
              conventional plan for each engagement.
            </p>
          </div>

          <div className="hidden grid-cols-[12rem_1fr_16rem] gap-6 border-b border-rule bg-sheet-edge/60 px-5 py-2.5 sm:px-7 lg:grid">
            <span className="cell-label text-ink-muted">Sector</span>
            <span className="cell-label text-ink-muted">Observation</span>
            <span className="cell-label text-ink-muted">Estimate saved</span>
          </div>

          <div className="divide-y divide-rule">
            {entries.map((entry) => (
              <article
                key={entry.sector}
                className="grid gap-4 px-5 py-5 sm:px-7 lg:grid-cols-[12rem_1fr_16rem] lg:gap-6 lg:py-6"
              >
                <h3
                  className="entry-title text-ink"
                  style={{ fontStretch: '80%' }}
                >
                  {entry.sector}
                </h3>

                <div>
                  <p className="text-base font-medium leading-snug text-ink">
                    {entry.outcome}
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {entry.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex gap-3 text-[0.9375rem] leading-relaxed text-ink-muted"
                      >
                        <span
                          className="mt-2.5 h-px w-3 shrink-0 bg-overprint"
                          aria-hidden="true"
                        />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
                    {entry.scope.map((tag) => (
                      <li key={tag} className="cell-label text-ink-muted">
                        {tag}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-4 border-t border-rule-faint pt-4 lg:border-t-0 lg:pt-0">
                  <div>
                    <p className="cell-label text-overprint">Time</p>
                    <p className="mt-1.5 text-[0.9375rem] font-medium leading-snug text-ink">
                      {entry.savings.time}
                    </p>
                  </div>
                  <div>
                    <p className="cell-label text-overprint">Cost</p>
                    <p className="mt-1.5 text-[0.9375rem] font-medium leading-snug text-ink">
                      {entry.savings.cost}
                    </p>
                  </div>
                  <p className="text-sm leading-relaxed text-ink-muted">
                    {entry.savings.basis}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
